import { type ErrorPolicy, type WatchQueryFetchPolicy, useMutation, useQuery } from '@apollo/client';

import MUTATION_SIMILARITY_CHECKS_SUBMIT from './MutationSimilarityChecksSubmit.graphql';
import QUERY_GET_SIMILARITY_CHECKS_BY_ID from './QueryGetSimilarityChecksById.graphql';
import QUERY_GET_SIMILARITY_CHECKS_BY_METADATA from './QueryGetSimilarityChecksByMetadata.graphql';
import type {
  MutationSimilarityChecksSubmitMutation,
  MutationSimilarityChecksSubmitMutationVariables,
} from './__generated__/MutationSimilarityChecksSubmit';
import type {
  QueryGetSimilarityChecksByIdQuery,
  QueryGetSimilarityChecksByIdQueryVariables,
} from './__generated__/QueryGetSimilarityChecksById';
import type {
  QueryGetSimilarityChecksByMetadataQuery,
  QueryGetSimilarityChecksByMetadataQueryVariables,
} from './__generated__/QueryGetSimilarityChecksByMetadata';

export type SimilarityCheckResult =
  | { __typename: 'SimilarityCheck_CompleteStatus'; reportUrl: string; score: number }
  | { __typename: 'SimilarityCheck_FailedStatus'; error: string }
  | { __typename: 'SimilarityCheck_InProgressStatus' };

/**
 * Hook to submit a new similarity check
 * @returns Mutation hook for submitting similarity checks
 */
export const useSimilarityCheckSubmit = () => {
  return useMutation<MutationSimilarityChecksSubmitMutation, MutationSimilarityChecksSubmitMutationVariables>(
    MUTATION_SIMILARITY_CHECKS_SUBMIT,
    { context: { clientName: 'gatewayGql' } }
  );
};

/**
 * Lazy hook to fetch similarity check results by metadata
 * @param metadata - The metadata input to query by
 * @param options - Additional query options
 * @returns Query result containing similarity check data
 */
export const useGetSimilarityChecksByMetadata = (
  metadata: QueryGetSimilarityChecksByMetadataQueryVariables['metadata'],
  options?: {
    skip?: boolean;
    notifyOnNetworkStatusChange?: boolean;
    fetchPolicy?: WatchQueryFetchPolicy;
    errorPolicy?: ErrorPolicy;
    onCompleted?: (data: QueryGetSimilarityChecksByMetadataQuery) => void;
  }
) => {
  return useQuery<QueryGetSimilarityChecksByMetadataQuery, QueryGetSimilarityChecksByMetadataQueryVariables>(
    QUERY_GET_SIMILARITY_CHECKS_BY_METADATA,
    {
      variables: { metadata },
      context: { clientName: 'gatewayGql' },
      pollInterval: undefined,
      ...options,
    }
  );
};

/**
 * Hook to fetch similarity check results by ID
 * @param similarityCheckId - The ID of the similarity check to query
 * @param options - Additional query options
 * @returns Query result containing similarity check data
 */
export const useGetSimilarityChecksById = (
  similarityCheckId: string,
  options?: { skip?: boolean; notifyOnNetworkStatusChange?: boolean; fetchPolicy?: WatchQueryFetchPolicy }
) => {
  return useQuery<QueryGetSimilarityChecksByIdQuery, QueryGetSimilarityChecksByIdQueryVariables>(
    QUERY_GET_SIMILARITY_CHECKS_BY_ID,
    {
      variables: { similarityCheckId },
      context: { clientName: 'gatewayGql' },
      ...options,
    }
  );
};
